import { z } from 'zod'

export const STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const stratalystFormsAuthoringReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  module_id: optionalQueryString,
}).passthrough()

export const stratalystFormsAuthoringReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.array(z.string()),
  projection: z.literal('stratalyst-consulting-forms-admin'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const stratalystFormsAuthoringCatalogReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.array(z.string()),
  projection: z.literal('stratalyst-form-catalog'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const stratalystFormsAuthoringDb1SideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.boolean(),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const stratalystFormsAuthoringAgentReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB2'),
  tablesRead: z.array(z.string()),
  projection: z.literal('stratalyst-consulting-agent-catalog'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const stratalystFormsAuthoringAgentSideEffectsSchema = z.object({
  db1Read: z.literal(false),
  db1Written: z.literal(false),
  db2Read: z.boolean(),
  db2Written: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const stratalystFormsAuthoringFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'textarea', 'number', 'select', 'checkbox', 'rating', 'date', 'multiselect']),
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() }).passthrough()).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
}).passthrough()

export const stratalystFormsAuthoringModuleStatusSchema = z.enum(['draft', 'published', 'archived'])
export const stratalystFormsAuthoringRevisionStatusSchema = z.enum(['draft', 'active', 'archived'])
export const stratalystFormsAuthoringAssignmentStatusSchema = z.enum(['active', 'revoked'])
export const stratalystFormsAuthoringRecipeStatusSchema = z.enum(['draft', 'active', 'archived'])

export const stratalystFormsAuthoringModuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: stratalystFormsAuthoringModuleStatusSchema,
  createdByUserId: z.string().nullable(),
  formCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const stratalystFormsAuthoringModuleFormSchema = z.object({
  id: z.string(),
  formRegistryId: z.string(),
  position: z.number(),
  promptId: z.string().nullable(),
  formKey: z.string(),
  displayName: z.string(),
  industryKey: z.string().nullable(),
}).passthrough()

export const stratalystFormsAuthoringRegistryFormSchema = z.object({
  id: z.string(),
  industryKey: z.string().nullable(),
  formKey: z.string(),
  externalFormId: z.string().nullable(),
  displayName: z.string(),
  metadata: z.record(z.unknown()),
  updatedAt: z.string(),
}).passthrough()

export const stratalystFormsAuthoringRevisionSchema = z.object({
  id: z.string(),
  formRegistryId: z.string(),
  version: z.number(),
  fields: z.array(stratalystFormsAuthoringFieldSchema),
  status: stratalystFormsAuthoringRevisionStatusSchema,
  createdByUserId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const stratalystFormsAuthoringAssignmentSchema = z.object({
  id: z.string(),
  moduleId: z.string(),
  orgId: z.string(),
  status: stratalystFormsAuthoringAssignmentStatusSchema,
  assignedByUserId: z.string().nullable(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
}).passthrough()

export const stratalystFormsAuthoringAgentSchema = z.object({
  name: z.string(),
  hasPrompt: z.literal(true),
}).passthrough()

export const stratalystFormsAuthoringRecipeStepSchema = z.object({
  id: z.string(),
  stepOrder: z.number(),
  parallelGroup: z.number(),
  promptId: z.string(),
  consumesStepOrder: z.number().nullable(),
  label: z.string().nullable(),
  successCriteria: z.string().nullable(),
  qcPromptId: z.string().nullable(),
}).passthrough()

export const stratalystFormsAuthoringRecipeSchema = z.object({
  id: z.string(),
  moduleId: z.string(),
  name: z.string(),
  status: stratalystFormsAuthoringRecipeStatusSchema,
  createdByUserId: z.string().nullable(),
  steps: z.array(stratalystFormsAuthoringRecipeStepSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const stratalystFormsAuthoringAgentCatalogResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  agents: z.array(stratalystFormsAuthoringAgentSchema),
  catalogAvailable: z.boolean(),
  readEvidence: stratalystFormsAuthoringAgentReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringAgentSideEffectsSchema,
}).passthrough()

export const stratalystFormsAuthoringAssignmentsResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  assignments: z.array(stratalystFormsAuthoringAssignmentSchema),
  readEvidence: stratalystFormsAuthoringReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}).passthrough()

export const stratalystFormsAuthoringFormRegistryResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  forms: z.array(stratalystFormsAuthoringRegistryFormSchema),
  readEvidence: stratalystFormsAuthoringCatalogReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}).passthrough()

export const stratalystFormsAuthoringFormRevisionsResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  revisions: z.array(stratalystFormsAuthoringRevisionSchema),
  readEvidence: stratalystFormsAuthoringReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}).passthrough()

export const stratalystFormsAuthoringModulesResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  modules: z.array(stratalystFormsAuthoringModuleSchema),
  readEvidence: stratalystFormsAuthoringReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}).passthrough()

export const stratalystFormsAuthoringModuleDetailResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  module: stratalystFormsAuthoringModuleSchema,
  forms: z.array(stratalystFormsAuthoringModuleFormSchema),
  readEvidence: stratalystFormsAuthoringReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}).passthrough()

export const stratalystFormsAuthoringModuleRecipesResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_FORMS_AUTHORING_READ_CONTRACT_VERSION),
  recipes: z.array(stratalystFormsAuthoringRecipeSchema),
  readEvidence: stratalystFormsAuthoringReadEvidenceSchema,
  sideEffects: stratalystFormsAuthoringDb1SideEffectsSchema,
}).passthrough()

export type StratalystFormsAuthoringReadQuery = z.infer<typeof stratalystFormsAuthoringReadQuerySchema>
export type StratalystFormsAuthoringReadEvidence = z.infer<typeof stratalystFormsAuthoringReadEvidenceSchema>
export type StratalystFormsAuthoringCatalogReadEvidence = z.infer<typeof stratalystFormsAuthoringCatalogReadEvidenceSchema>
export type StratalystFormsAuthoringDb1SideEffects = z.infer<typeof stratalystFormsAuthoringDb1SideEffectsSchema>
export type StratalystFormsAuthoringAgentReadEvidence = z.infer<typeof stratalystFormsAuthoringAgentReadEvidenceSchema>
export type StratalystFormsAuthoringAgentSideEffects = z.infer<typeof stratalystFormsAuthoringAgentSideEffectsSchema>
export type StratalystFormsAuthoringField = z.infer<typeof stratalystFormsAuthoringFieldSchema>
export type StratalystFormsAuthoringModule = z.infer<typeof stratalystFormsAuthoringModuleSchema>
export type StratalystFormsAuthoringModuleForm = z.infer<typeof stratalystFormsAuthoringModuleFormSchema>
export type StratalystFormsAuthoringRegistryForm = z.infer<typeof stratalystFormsAuthoringRegistryFormSchema>
export type StratalystFormsAuthoringRevision = z.infer<typeof stratalystFormsAuthoringRevisionSchema>
export type StratalystFormsAuthoringAssignment = z.infer<typeof stratalystFormsAuthoringAssignmentSchema>
export type StratalystFormsAuthoringAgent = z.infer<typeof stratalystFormsAuthoringAgentSchema>
export type StratalystFormsAuthoringRecipeStep = z.infer<typeof stratalystFormsAuthoringRecipeStepSchema>
export type StratalystFormsAuthoringRecipe = z.infer<typeof stratalystFormsAuthoringRecipeSchema>
export type StratalystFormsAuthoringAgentCatalogResponse = z.infer<typeof stratalystFormsAuthoringAgentCatalogResponseSchema>
export type StratalystFormsAuthoringAssignmentsResponse = z.infer<typeof stratalystFormsAuthoringAssignmentsResponseSchema>
export type StratalystFormsAuthoringFormRegistryResponse = z.infer<typeof stratalystFormsAuthoringFormRegistryResponseSchema>
export type StratalystFormsAuthoringFormRevisionsResponse = z.infer<typeof stratalystFormsAuthoringFormRevisionsResponseSchema>
export type StratalystFormsAuthoringModulesResponse = z.infer<typeof stratalystFormsAuthoringModulesResponseSchema>
export type StratalystFormsAuthoringModuleDetailResponse = z.infer<typeof stratalystFormsAuthoringModuleDetailResponseSchema>
export type StratalystFormsAuthoringModuleRecipesResponse = z.infer<typeof stratalystFormsAuthoringModuleRecipesResponseSchema>
